/**
 * PATH: packages/email-hub/src/feature/components/email-preview-pane/usePreviewShortcuts.ts
 * FILE_ID: EMAILHUB-EMAILPREVIEWPANE-SHORTCUTS-HOOK-001
 * PURPOSE: Own preview keyboard shortcuts without coupling them to markup.
 */
import { useEffect } from 'react'
import { buildKanbanBoardLink } from '../../lib/kanbanDeepLink'
import type { Email } from '../../hooks/useEmails'
import type { KanbanApi } from './types'

interface PreviewShortcutOptions {
  email: Email
  kanbanApi: KanbanApi
  archive: ReturnType<
    typeof import('../../hooks/useEmails').useEmailActions
  >['archive']
  toast: ReturnType<
    typeof import('../../../_internal/components/Toast').useToast
  >['toast']
  navigate: (to: string) => void
  onRefetch?: () => void
  onResolved?: (emailId: string) => void
  setPingOpen: (open: boolean) => void
  setPeekBoardOpen: (open: boolean) => void
  setAllocateOpen: (open: boolean) => void
}

function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null
  const tag = (element?.tagName || '').toLowerCase()
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    Boolean(element?.isContentEditable)
  )
}

export function usePreviewShortcuts({
  email,
  archive,
  toast,
  navigate,
  onRefetch,
  onResolved,
  setPingOpen,
  setPeekBoardOpen,
  setAllocateOpen,
}: PreviewShortcutOptions): void {
  useEffect(() => {
    const onPingKey = (event: KeyboardEvent) => {
      if (event.key !== '@' || isTypingTarget(event.target)) return
      event.preventDefault()
      setPingOpen(true)
    }
    document.addEventListener('keydown', onPingKey)
    return () => document.removeEventListener('keydown', onPingKey)
  }, [setPingOpen])

  useEffect(() => {
    const onActionKey = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase()
      if (!['a', 'b', 'd'].includes(key)) return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isTypingTarget(event.target)) return

      if (key === 'b') {
        if (email.allocation_summary?.board_id) {
          event.preventDefault()
          setPeekBoardOpen(true)
          return
        }
        const link = buildKanbanBoardLink(email.allocation_summary)
        if (link) {
          event.preventDefault()
          navigate(link)
        }
        return
      }

      if (key === 'a') {
        event.preventDefault()
        event.stopPropagation()
        setAllocateOpen(true)
        return
      }

      event.preventDefault()
      event.stopPropagation()
      onResolved?.(email.id)
      void archive(email.id, {
        folder: email.folder,
        isRead: email.is_read,
        isStarred: email.is_starred,
        complete: true,
      })
        .then(() => {
          toast('Done - archived and card marked done', 'success')
          onRefetch?.()
        })
        .catch(() => {
          toast('Could not mark done', 'error')
          onRefetch?.()
        })
    }
    document.addEventListener('keydown', onActionKey)
    return () => document.removeEventListener('keydown', onActionKey)
  }, [
    archive,
    email,
    navigate,
    onRefetch,
    onResolved,
    setAllocateOpen,
    setPeekBoardOpen,
    toast,
  ])
}
